import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import AuthLayout from "@/components/layout/AuthLayout";
import { PasswordInput } from "@/components/common/PasswordInput";
import { FormAlert } from "@/components/common/Alert";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Setup() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [needsSetup, setNeedsSetup] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: typeof v === "string" ? v : v.target.value }));

  useEffect(() => {
    api.get("/auth/setup-status").then(({ data }) => setNeedsSetup(data.needs_setup)).catch(() => setNeedsSetup(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 12) return setError("Use at least 12 characters.");
    if (form.password !== form.confirm) return setError("The two passwords do not match.");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/setup", { name: form.name, email: form.email, password: form.password });
      setUser(data);
      navigate("/welcome", { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (needsSetup === false) {
    return (
      <AuthLayout title="Setup is complete" testid="setup-locked">
        <p className="text-sm text-muted-foreground">This hub already has an Owner, so the setup screen is locked.</p>
        <Button asChild className="mt-6 w-full"><Link to="/signin" data-testid="setup-locked-signin-link">Go to sign in</Link></Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Set up the hub" subtitle="Create the first Owner account. You will be signed in straight away." testid="setup-page">
      {needsSetup === null ? (
        <div className="space-y-4"><Skeleton className="h-10 rounded-xl" /><Skeleton className="h-10 rounded-xl" /></div>
      ) : (
        <form onSubmit={submit} className="space-y-5">
          <FormAlert testid="setup-error">{error}</FormAlert>
          <div className="space-y-2">
            <Label htmlFor="name">Your name</Label>
            <Input id="name" value={form.name} onChange={set("name")} maxLength={120} required data-testid="setup-name-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Work email</Label>
            <Input id="email" type="email" autoComplete="username" value={form.email} onChange={set("email")} required data-testid="setup-email-input" />
          </div>
          <PasswordInput id="password" label="Password" value={form.password} onChange={set("password")} autoComplete="new-password" hint="At least 12 characters. Avoid common passwords." testid="setup-password-input" />
          <PasswordInput id="confirm" label="Type it again" value={form.confirm} onChange={set("confirm")} autoComplete="new-password" testid="setup-confirm-input" />
          <Button type="submit" className="w-full" disabled={busy || !form.name || !form.email || !form.password} data-testid="setup-submit-button">
            {busy ? "Creating" : "Create Owner account"}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
